import { Injectable } from '@angular/core';
import { UntypedFormArray, UntypedFormGroup, Validators } from '@angular/forms';
import { PrFormAbstract } from '@app/components/pr-form/pr-form.abstract';
import {
  Parameters,
  PledgeProductRead,
} from '@app/forms/subscription/definition/interface/Pledge';
import { FormConfig } from '@app/forms/subscription/definition/types/subscription';
import FormGroupArray from '@app/forms/subscription/services/FormGroupArray';
import { Big } from 'big.js';
import * as _ from 'lodash';

// PRE-4896 : builder dédié à la slide "Taux par support" (arbitrage). Combine les supports de
// réinvestissement en stock et en flux (mutuellement exclusifs selon arbitrageType, cf.
// reinvestment-stock-form / reinvestment-flux-form) dans un seul tableau affiché, en conservant
// tous les champs déjà présents sur chaque support (repartitionStock, amountRepartitionStock, ...) —
// à l'identique du pattern déjà utilisé pour préserver ces champs dans isin-table. Le champ interne
// "_source" indique de quelle liste chaque support provient, pour recomposer les deux listes
// d'origine à l'extraction.
//
// Les 4 champs "*Saisi" sont affichés via un input brut (eaNumericInput), le FormControl porte donc
// directement la valeur affichée en pourcentage (0-100), d'où la conversion *100/100 à
// l'initialisation/l'extraction (toPercent/fromPercent), à l'identique de taux-par-support.
// Big plutôt que Math.round : évite le cas classique où l'étape intermédiaire *100000 est elle-même
// imprécise en flottant et fait arrondir dans le mauvais sens (cf. isin-table/ea-subscription-form.builder.ts).
function toPercent(value: number | null | undefined): number | null {
  return value != null ? new Big(value).times(100).round(3).toNumber() : null;
}
function fromPercent(value: number | string | null | undefined): number | null {
  return value != null && value !== '' ? +value / 100 : null;
}

@Injectable({ providedIn: 'root' })
export abstract class EaSubscriptionBuilder extends PrFormAbstract {
  generateForm(entity: PledgeProductRead & { parameters: any }, option) {
    const parameters: Parameters = { ...option };
    const options = {};

    const rows: UntypedFormGroup[] = [];
    const stock = entity?.parameters?.supportsInvestmentStock || [];
    const flux = entity?.parameters?.supportsInvestmentFlux || [];

    _.forEach(stock, item =>
      rows.push(this.formBuilder.group(this.createModel(item, 'stock'))),
    );
    _.forEach(flux, item =>
      rows.push(this.formBuilder.group(this.createModel(item, 'flux'))),
    );

    const formConfig: FormConfig<PledgeProductRead & { parameters: any }> = {
      id: [entity.id],
      type: [entity.type],
      productId: [entity.productId],
      productName: [entity.productName],
      status: [entity.status],
      parameters: this.formBuilder.group({
        supportRepartition: this.formBuilder.array(
          rows,
          Validators.nullValidator,
        ),
      }),
    };

    return this.formBuilder.group(formConfig, options);
  }

  generate(entity: any, option) {
    return FormGroupArray.generate({
      entity,
      options: option,
      formBuilder: this.formBuilder,
      generateForm: this.generateForm.bind(this),
      propertyName: 'products',
    });
  }

  get products() {
    return this.form.get('products') as UntypedFormArray;
  }
  get supportRepartition() {
    return this.products
      .get(`${0}`)
      .get('parameters')
      .get('supportRepartition') as UntypedFormArray;
  }

  public createModel(item, source: 'stock' | 'flux') {
    return {
      _source: [source],
      id: [item ? item.id : null],
      name: [item ? item.name : null],
      type: [item ? item.type : null],
      isin: [item ? item.isin : null],
      index: [item?.index ?? null],
      details: [item ? item.details : null],
      repartitionStock: [item?.repartitionStock ?? null],
      repartitionFlux: [item?.repartitionFlux ?? null],
      amountRepartitionStock: [item?.amountRepartitionStock ?? null],
      amountRepartitionFlux: [item?.amountRepartitionFlux ?? null],
      currentAllocationAmountStock: [
        item?.currentAllocationAmountStock ?? null,
      ],
      // Préremplis par la commande COMPLETE_SUPPORT_RATES (résolution base support), jamais saisis
      // directement — servent de placeholder et de valeur par défaut du champ "Saisi" ci-dessous.
      upfrontAssureurAutocomplete: [item?.upfrontAssureurAutocomplete ?? null],
      upfrontEmetteurAutocomplete: [item?.upfrontEmetteurAutocomplete ?? null],
      fraisGestionAutocomplete: [item?.fraisGestionAutocomplete ?? null],
      retrocessionAssureurAutocomplete: [
        item?.retrocessionAssureurAutocomplete ?? null,
      ],
      // Valeur effectivement retenue : initialisée depuis la valeur déjà saisie (rejeu du
      // formulaire) sinon depuis la valeur préremplie ci-dessus, convertie en pourcentage (0-100)
      // pour l'affichage. Désactivée en lecture seule pour les organisations affiliées EUODIA (cf.
      // taux-par-support-arbitrage.component.ts).
      upfrontAssureurSaisi: [
        toPercent(item?.upfrontAssureurSaisi ?? item?.upfrontAssureurAutocomplete),
      ],
      upfrontEmetteurSaisi: [
        toPercent(item?.upfrontEmetteurSaisi ?? item?.upfrontEmetteurAutocomplete),
      ],
      fraisGestionSaisi: [
        toPercent(item?.fraisGestionSaisi ?? item?.fraisGestionAutocomplete),
      ],
      retrocessionAssureurSaisi: [
        toPercent(
          item?.retrocessionAssureurSaisi ??
            item?.retrocessionAssureurAutocomplete,
        ),
      ],
    };
  }

  extract() {
    const products = _.cloneDeep(this.form.value.products);
    products.forEach(product => {
      const rows = product.parameters.supportRepartition || [];

      const toStored = (row: any) => {
        const { _source, ...rest } = row;
        return {
          ...rest,
          upfrontAssureurSaisi: fromPercent(row.upfrontAssureurSaisi),
          upfrontEmetteurSaisi: fromPercent(row.upfrontEmetteurSaisi),
          fraisGestionSaisi: fromPercent(row.fraisGestionSaisi),
          retrocessionAssureurSaisi: fromPercent(
            row.retrocessionAssureurSaisi,
          ),
        };
      };

      product.parameters.supportsInvestmentStock = rows
        .filter(row => row._source === 'stock')
        .map(toStored);
      product.parameters.supportsInvestmentFlux = rows
        .filter(row => row._source === 'flux')
        .map(toStored);
      product.parameters.supportRepartition = undefined;
    });
    return products;
  }
}
